import { Injectable } from '@angular/core';
import { Firestore, doc, setDoc, Timestamp } from '@angular/fire/firestore';
import { timer, Subscription } from 'rxjs';
import { FirebaseAuthService } from '../providers/firebase-auth.service';
import { BrowserFingerprintService } from './browser-fingerprint.service';
import { DeviceDetectionService } from './device-detection.service';
import { ICard } from 'src/app/models/card';

/**
 * Interface for active view session
 */
interface ViewSession {
  card: ICard;
  startTime: number;
  subscription: Subscription;
}

/**
 * VideoAnalyticsService
 *
 * Simplified service for tracking 3D video model views.
 *
 * Logic: Wait 1.8s after card becomes visible, then send event to Firestore.
 * No pause tracking, no duration tracking - just fact of view.
 *
 * Features:
 * - Track view fact (after 1.8s animation)
 * - Browser fingerprinting for anonymous users
 * - Direct writes to Firestore
 */
@Injectable({
  providedIn: 'root'
})
export class VideoAnalyticsService {
  private activeSessions = new Map<string, ViewSession>();
  private sessionId: string;
  private viewedModelsInSession = new Set<string>();

  constructor(
    private firestore: Firestore,
    private authService: FirebaseAuthService,
    private fingerprintService: BrowserFingerprintService,
    private deviceService: DeviceDetectionService
  ) {
    this.sessionId = this.generateSessionId();
    console.log('[VideoAnalytics] Service initialized. Session ID:', this.sessionId);
  }

  /**
   * Start tracking model view
   * Call when video becomes visible (IntersectionStatus.Visible)
   *
   * @param card - model card
   */
  startTracking(card: ICard): void {
    if (this.activeSessions.has(card.id)) {
      console.warn('[VideoAnalytics] Already tracking:', card.id);
      return;
    }

    const startTime = Date.now();

    // Simple timer: Wait 1800ms (animation duration), then send event
    const subscription = timer(1800).subscribe(() => {
      console.log('[VideoAnalytics] ⏱️ 1.8s passed, sending event:', card.id);
      this.sendToFirestore(card, startTime);

      // Clean up session after sending
      this.activeSessions.delete(card.id);
    });

    const session: ViewSession = {
      card,
      startTime,
      subscription
    };

    this.activeSessions.set(card.id, session);
    console.log('[VideoAnalytics] Started tracking:', card.id);
  }

  /**
   * Stop tracking (card scrolled away before 1.8s)
   * Cancel timer if view was too short
   *
   * @param cardId - model card ID
   */
  stopTracking(cardId: string): void {
    const session = this.activeSessions.get(cardId);
    if (!session) {
      return;
    }

    console.log('[VideoAnalytics] Stopping tracking (cancelled):', cardId);

    // Cancel timer
    session.subscription.unsubscribe();

    // Clean up session
    this.activeSessions.delete(cardId);
  }

  /**
   * Send event to Firestore (simplified - just fact of view)
   */
  private async sendToFirestore(
    card: ICard,
    startTime: number
  ): Promise<void> {
    try {
      const viewerId = this.getViewerId();
      const isAuth = !!this.authService.currentUser;
      const viewId = this.generateViewId();

      // Check: first view of this model in current session?
      const isFirstView = !this.viewedModelsInSession.has(card.id);
      this.viewedModelsInSession.add(card.id);

      const docRef = doc(this.firestore, `video_analytics/${card.id}/views/${viewId}`);

      const eventData = {
        view_id: viewId,
        model_id: card.id,
        viewer_uid: viewerId,
        is_authenticated: isAuth,
        view_timestamp: Timestamp.fromMillis(startTime),
        device_type: this.deviceService.getDeviceType(),
        viewer_subscription_status: this.getSubscriptionStatus(),
        session_id: this.sessionId,
        is_first_view_in_session: isFirstView,
      };

      await setDoc(docRef, eventData);

      console.log('[VideoAnalytics] ✅ Event sent:', card.id);
    } catch (error) {
      console.error('[VideoAnalytics] ❌ Failed to send event:', error);
    }
  }

  /**
   * Get viewer_uid (Firebase UID or browser fingerprint)
   */
  private getViewerId(): string {
    const user = this.authService.currentUser;
    if (user?.uid) {
      return user.uid;
    }

    // Anonymous user - use fingerprint
    return this.fingerprintService.getFingerprint();
  }

  /**
   * Get subscription status
   */
  private getSubscriptionStatus(): string {
    const user = this.authService.currentUser;
    if (!user) {
      return 'none';
    }

    // Assuming you have subscriptionStatus in currentUser
    return user.subscriptionStatus || 'none';
  }

  /**
   * Generate view_id in format: timestamp_shortuid
   */
  private generateViewId(): string {
    const timestamp = Date.now();
    const shortUid = Math.random().toString(36).substring(2, 6);
    return `${timestamp}_${shortUid}`;
  }

  /**
   * Generate session_id
   */
  private generateSessionId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 11);
    return `sess_${timestamp}_${random}`;
  }
}
